import { JSX, startTransition } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import DetailFavoriteButton from "@/components/detail/DetailFavoriteButton";
import Tag from "@/components/helpers/Tag.tsx";
import { IconImdb, IconKinopoisk, IconSplaySmall } from "@/components/icons/main.icons";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { Banner } from "@/api/content/content.types";
import { ROUTES } from "@/config/routes.ts";
import { useGetContentEpisodes } from "@/hooks/api/content.hooks";
import { useGetEpisodeHistory } from "@/hooks/api/feed.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn";

type MovieBannerTitleProps = Pick<
  Banner,
  | "age_restrictions"
  | "country"
  | "description"
  | "genres"
  | "logo_image"
  | "year"
  | "rating"
  | "logo_image_rectangle"
  | "imdb_rating"
  | "showFavorite"
  | "kinopoisk_rating"
  | "id"
  | "is_serial"
> & {
  firstSeason?: number;
};

const MovieBannerTitle = ({
  age_restrictions,
  country: countries,
  description,
  genres,
  logo_image,
  year,
  rating,
  imdb_rating,
  kinopoisk_rating,
  logo_image_rectangle,
  id,
  is_serial,
  firstSeason = 1,
  showFavorite = true,
}: MovieBannerTitleProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: episodes } = useGetContentEpisodes(+firstSeason);
  const token = localStorage.getItem("splay:token");

  const { openModal } = useModalRouter();

  const { data: history } = useGetEpisodeHistory(id, showFavorite);

  const openMovie = () => {
    startTransition(() => {
      if (!token) {
        openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_LOGIN]);
      } else {
        const episodeId = history?.episodes?.[0]?.episode_id || episodes?.episodes?.[0]?.id;
        const route = !is_serial
          ? ROUTES.DETAIL.EPISODE.replace(":id", String(id))
          : !episodes
            ? ROUTES.DETAIL.MAIN.replace(":id", String(id))
            : ROUTES.DETAIL.SERIAL_EPISODE.replace(":id", String(id)).replace(":episodeId", String(episodeId));
        navigate(route);
      }
    });
  };

  const logo = logo_image_rectangle?.small ?? logo_image?.small;

  return (
    <div
      className={cn(
        "absolute left-[3.625rem] top-1/2 z-[1] w-full max-w-[28.75rem] -translate-y-1/2 transform space-y-[1.875rem] max-xl:left-8 max-xl:space-y-5 max-md:left-0",
        {
          "max-md:bottom-4 max-md:left-4 max-md:top-auto max-md:translate-y-0": pathname === "/",
        },
      )}
    >
      <div className={"space-y-5 max-xl:space-y-3 max-lg:space-y-[.625rem]"}>
        <Link to={ROUTES.DETAIL.MAIN.replace(":id", String(id))}>
          {logo && (
            <img
              src={logo}
              alt={"movie-title"}
              loading={"lazy"}
              className={"h-[7.5rem] w-auto object-left max-2xl:h-16 max-lg:h-10"}
            />
          )}
        </Link>
        <ul className="mr-8 mt-1 flex items-center gap-4 max-lg:mt-0">
          {rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconSplaySmall className="size-8 fill-orange-1 max-lg:size-5" />
              <span className="text-[24px] font-medium max-lg:text-[14px]"> {rating?.toFixed(1)}</span>
            </li>
          )}
          {kinopoisk_rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconKinopoisk className="size-8 max-lg:size-5" />
              <span className="text-[24px] font-medium max-lg:text-[14px]">{kinopoisk_rating?.toFixed(1)}</span>
            </li>
          )}
          {imdb_rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconImdb className="size-8 max-lg:size-5" />
              <span className="text-[24px] font-medium max-lg:text-[14px]">{imdb_rating?.toFixed(1)}</span>
            </li>
          )}
        </ul>
        <ul className={"flex h-[1.875rem] items-center gap-[0.625rem] max-lg:flex-wrap max-lg:gap-2"}>
          {year && (
            <li>
              <Link to={ROUTES.MAIN.YEAR.replace(":year", year.toString())}>
                <Tag
                  type={"square"}
                  className={
                    "!border-[0px] bg-black/40  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                  }
                >
                  {year.toString()}
                </Tag>
              </Link>
            </li>
          )}
          {countries && (
            <li>
              <Tag
                type={"square"}
                className={
                  "text-nowrap !border-[0px] bg-black/40  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                }
              >
                {countries.slice(0, 2).map((country, index) => (
                  <span
                    key={country.id}
                    className="cursor-pointer"
                    onClick={() => {
                      startTransition(() => {
                        navigate(ROUTES.MAIN.COUNTRY.replace(":id", String(country.id)));
                      });
                    }}
                  >
                    {country.name} {index === 0 && countries.length > 1 ? " / " : ""}
                  </span>
                ))}
              </Tag>
            </li>
          )}
          {genres && (
            <li>
              <Tag
                type={"square"}
                className={
                  "text-nowrap !border-[0px] bg-black/40  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                }
              >
                {genres.slice(0, 2).map((genre, index) => (
                  <Link to={ROUTES.MAIN.GENRE.replace(":id", String(genre.id))} key={genre.id}>
                    {genre.name} {index === 0 && genres.length > 1 ? " / " : ""}
                  </Link>
                ))}
              </Tag>
            </li>
          )}
          <li>
            <Tag
              type={"square"}
              className={"!border-[0px] bg-black/40  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"}
            >
              +{age_restrictions}
            </Tag>
          </li>
        </ul>
        <p className={"line-clamp-3 text-lg leading-6 max-xl:text-[12px] max-lg:hidden"}>{description}</p>
      </div>
      <div className={cn("flex items-center gap-[0.625rem]", { "max-md:hidden": pathname == "/" })}>
        <Button
          onClick={openMovie}
          size="medium"
          title={t("Actions.start-watching")}
          className={"text-nowrap px-5 max-xl:px-3 max-lg:max-w-[70%] max-lg:py-2"}
        >
          {(history?.time_code || history?.episodes?.length || 0) > 0
            ? t("Actions.continue-watching")
            : t("Actions.start-watching")}
        </Button>
        <div className="w-12">
          {token && showFavorite && (
            <DetailFavoriteButton
              id={id}
              className="h-8 w-8 bg-[#FFFFFF33] px-[0.4rem] hover:bg-primary lg:h-12 lg:w-12 lg:px-[0.6rem]"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieBannerTitle;
